// @ts-check
// Anexos das mensagens: imagem com prévia e lightbox, nota de voz com player próprio sobre <audio>,
// documento como chip (ícone, nome, tamanho, baixar), figurinha e vídeo.
import { html } from 'htm/preact';
import { createPortal } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { formatDuration, formatFileSize, getFileVisual } from '../../lib/conversation-format.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/conversations.js').MessageAttachment} MessageAttachment */

/**
 * @param {{ attachment: MessageAttachment, onClose: () => void }} props
 */
function ImageLightbox({ attachment, onClose }) {
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);
  return createPortal(
    html`<div class="image-lightbox" role="dialog" aria-modal="true" aria-label=${attachment.fileName} onClick=${onClose}>
      <div class="image-lightbox-bar" onClick=${(event) => event.stopPropagation()}>
        <span class="image-lightbox-name">${attachment.fileName}</span>
        <a class="image-lightbox-action" href=${attachment.url} download=${attachment.fileName} title=${t('conversations.media.download')} aria-label=${t('conversations.media.download')}>
          <${Icon} name="IconDownload" size=${16} />
        </a>
        <button type="button" class="image-lightbox-action" title=${t('conversations.media.closeImage')} aria-label=${t('conversations.media.closeImage')} onClick=${onClose}>
          <${Icon} name="IconX" size=${16} />
        </button>
      </div>
      <img class="image-lightbox-image" src=${attachment.url} alt=${attachment.fileName} onClick=${(event) => event.stopPropagation()} />
    </div>`,
    document.body,
  );
}

/**
 * @param {{ attachment: MessageAttachment }} props
 */
export function ImageAttachment({ attachment }) {
  const [isOpen, setIsOpen] = useState(false);
  return html`<button type="button" class="message-image" title=${t('conversations.media.openImage')} aria-label=${t('conversations.media.openImage')} onClick=${() => setIsOpen(true)}>
      <img src=${attachment.url} alt=${attachment.fileName} loading="lazy" />
    </button>
    ${isOpen ? html`<${ImageLightbox} attachment=${attachment} onClose=${() => setIsOpen(false)} />` : null}`;
}

/**
 * Player compacto: play/pausa, barra de progresso clicável, duração e ícone de microfone.
 * @param {{ attachment: MessageAttachment }} props
 */
export function VoiceNotePlayer({ attachment }) {
  const audioRef = useRef(/** @type {HTMLAudioElement | null} */ (null));
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Alguns contêineres (webm, ogg gravados no celular) só informam a duração depois de um seek.
  const readDuration = (/** @type {HTMLAudioElement} */ audio) => {
    if (Number.isFinite(audio.duration)) {
      setDuration(audio.duration);
      return;
    }
    const handleDurationChange = () => {
      if (!Number.isFinite(audio.duration)) return;
      setDuration(audio.duration);
      audio.currentTime = 0;
      audio.removeEventListener('durationchange', handleDurationChange);
    };
    audio.addEventListener('durationchange', handleDurationChange);
    audio.currentTime = 1e7;
  };

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      // Uma nota de voz por vez, como no WhatsApp.
      document.querySelectorAll('audio[data-voice-note]').forEach((element) => {
        if (element !== audio) /** @type {HTMLAudioElement} */ (element).pause();
      });
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  return html`<div class="voice-note" data-playing=${isPlaying ? 'true' : undefined}>
    <button type="button" class="voice-note-toggle" aria-label=${isPlaying ? t('conversations.media.pause') : t('conversations.media.play')} onClick=${togglePlayback}>
      <${Icon} name=${isPlaying ? 'IconPlayerPause' : 'IconPlayerPlay'} size=${16} />
    </button>
    <div class="voice-note-body">
      <div
        class="voice-note-track"
        role="slider"
        tabindex="0"
        aria-label=${t('conversations.media.voiceNote')}
        aria-valuemin="0"
        aria-valuemax=${Math.round(duration)}
        aria-valuenow=${Math.round(currentTime)}
        onClick=${(event) => {
          const audio = audioRef.current;
          if (!audio || !duration) return;
          const rectangle = /** @type {HTMLElement} */ (event.currentTarget).getBoundingClientRect();
          audio.currentTime = Math.max(0, Math.min(1, (event.clientX - rectangle.left) / rectangle.width)) * duration;
        }}
        onKeyDown=${(event) => {
          const audio = audioRef.current;
          if (!audio || !duration) return;
          if (event.key === 'ArrowRight') audio.currentTime = Math.min(duration, audio.currentTime + 2);
          if (event.key === 'ArrowLeft') audio.currentTime = Math.max(0, audio.currentTime - 2);
        }}
      >
        <span class="voice-note-track-fill" style=${{ width: `${progress * 100}%` }}></span>
        <span class="voice-note-track-thumb" style=${{ left: `${progress * 100}%` }}></span>
      </div>
      <span class="voice-note-time">${formatDuration(isPlaying || currentTime > 0 ? currentTime : duration)}</span>
    </div>
    <span class="voice-note-microphone" title=${t('conversations.media.voiceNote')}><${Icon} name="IconMicrophone" size=${16} /></span>
    <audio
      ref=${audioRef}
      data-voice-note="true"
      src=${attachment.url}
      preload="metadata"
      onLoadedMetadata=${(event) => readDuration(/** @type {HTMLAudioElement} */ (event.currentTarget))}
      onTimeUpdate=${(event) => setCurrentTime(/** @type {HTMLAudioElement} */ (event.currentTarget).currentTime)}
      onPlay=${() => setIsPlaying(true)}
      onPause=${() => setIsPlaying(false)}
      onEnded=${(event) => {
        setIsPlaying(false);
        setCurrentTime(0);
        /** @type {HTMLAudioElement} */ (event.currentTarget).currentTime = 0;
      }}
    ></audio>
  </div>`;
}

/**
 * Chip de arquivo: ícone com a cor da categoria, nome, tamanho e botão de baixar.
 * @param {{ attachment: Pick<MessageAttachment, 'fileName' | 'mimeType' | 'size' | 'url'>, isUploading?: boolean, onRemove?: () => void }} props
 */
export function DocumentChip({ attachment, isUploading = false, onRemove }) {
  const visual = getFileVisual(attachment.fileName, attachment.mimeType);
  const extension = attachment.fileName.includes('.') ? attachment.fileName.split('.').pop()?.toUpperCase() : '';
  return html`<div class="document-chip" data-uploading=${isUploading ? 'true' : undefined}>
    <span class="document-chip-icon" style=${{ background: `var(--t-color-${visual.color})` }}><${Icon} name=${visual.icon} size=${16} /></span>
    <span class="document-chip-texts">
      <a class="document-chip-name" href=${attachment.url || undefined} target="_blank" rel="noopener noreferrer" title=${attachment.fileName}>${attachment.fileName}</a>
      <span class="document-chip-meta">${isUploading ? t('conversations.composer.uploading') : [extension, formatFileSize(attachment.size)].filter(Boolean).join(' · ')}</span>
    </span>
    ${onRemove
      ? html`<button type="button" class="document-chip-action" aria-label=${t('conversations.composer.removeAttachment')} title=${t('conversations.composer.removeAttachment')} onClick=${onRemove}>
          <${Icon} name="IconX" size=${14} />
        </button>`
      : attachment.url
        ? html`<a class="document-chip-action" href=${attachment.url} download=${attachment.fileName} aria-label=${t('conversations.media.download')} title=${t('conversations.media.download')}>
            <${Icon} name="IconDownload" size=${14} />
          </a>`
        : null}
  </div>`;
}

/**
 * @param {{ attachment: MessageAttachment }} props
 */
export function MessageAttachmentView({ attachment }) {
  if (attachment.kind === 'STICKER') {
    return html`<img class="message-sticker" src=${attachment.url} alt=${t('conversations.media.sticker')} loading="lazy" />`;
  }
  if (attachment.kind === 'IMAGE') return html`<${ImageAttachment} attachment=${attachment} />`;
  if (attachment.kind === 'AUDIO' && attachment.isVoiceNote) return html`<${VoiceNotePlayer} attachment=${attachment} />`;
  if (attachment.kind === 'AUDIO') return html`<audio class="message-audio" src=${attachment.url} controls preload="metadata"></audio>`;
  if (attachment.kind === 'VIDEO') {
    return html`<video class="message-video" src=${attachment.url} controls preload="metadata" aria-label=${t('conversations.media.video')}></video>`;
  }
  return html`<${DocumentChip} attachment=${attachment} />`;
}
